import { OtrosCargosElement, OtrosCargosIdentificacion } from "@/atv/eInvoiceApi";
import {
    OTHER_CHARGE_TYPE_OPTIONS,
    OTHER_CHARGE_TYPE_OTHERS,
    OTHER_CHARGE_TYPE_SERVICE_TAX,
    OTHER_CHARGE_TYPE_THIRD_PARTY,
    getOtherChargeTypeLabel,
} from "./Constants";
import { toNumberFormat } from "./Utils";
import { OtherChargeFormData } from "@/formTypes/OtherChargeFormDataType";
import { EDOCUMENT_TEXT_MAX_LENGTH, sanitizeSafeText } from "@amplifyShared/safeText";

/**
 * Modo en el que el usuario definio el monto del cargo. Un cargo porcentual se
 * recalcula cuando cambian las lineas; uno de monto fijo nunca se toca.
 */
export type OtherChargeAmountMode = "percent" | "amount";

/**
 * Lo que se persiste en el campo `otros_cargos` del documento: el nodo del XML
 * enriquecido con metadatos locales. Mismo patron que StoredReference.
 */
export type StoredOtherCharge = OtrosCargosElement & {
    id: string;
    mode: OtherChargeAmountMode;
    /**
     * Flujo que creo el cargo, cuando no fue la captura manual. Es metadato local igual
     * que `id` y `mode`: se persiste en `otros_cargos` pero nunca viaja a Hacienda,
     * porque `toOtrosCargosElement` arma el nodo campo por campo.
     */
    source?: OtherChargeSource;
};

/** Origen conocido de un cargo creado por un flujo propio y no a mano. */
export type OtherChargeSource = "shipping";

/** maxOccurs="15" del elemento OtrosCargos (XSD v4.4). */
export const MAX_OTHER_CHARGES = 15;
/**
 * maxLength del elemento Detalle. Es el mismo limite que el <Detalle> de una linea, asi
 * que se toma de la utilidad compartida en vez de repetir el numero.
 */
export const OTHER_CHARGE_DETALLE_MAX_LENGTH = EDOCUMENT_TEXT_MAX_LENGTH;
/** minLength/maxLength de TipoDocumentoOTROS. */
export const OTHER_CHARGE_OTROS_MIN_LENGTH = 5;
export const OTHER_CHARGE_OTROS_MAX_LENGTH = 100;
/** minLength/maxLength de NombreTercero. */
export const OTHER_CHARGE_NOMBRE_TERCERO_MIN_LENGTH = 5;
export const OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH = 100;
/** maxLength de IdentificacionTercero/Numero. */
export const OTHER_CHARGE_IDENTIFICACION_MAX_LENGTH = 20;
/** fractionDigits de PorcentajeOC. */
export const OTHER_CHARGE_PERCENT_MAX_FRACTION_DIGITS = 5;

/**
 * El nombre del tercero se mide recortado, que es el que se emite: `.min()` de Yup contaria
 * los espacios y dejaria pasar un "FABA " de cuatro letras, que Hacienda rechaza con
 * `cvc-minLength-valid` sobre NombreTerceroOtrosCargosType cuando el consecutivo ya se quemo.
 */
export const isValidThirdPartyName = (nombre: string | null | undefined): boolean => {
    const value = `${nombre || ""}`.trim();
    return value.length >= OTHER_CHARGE_NOMBRE_TERCERO_MIN_LENGTH
        && value.length <= OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH;
};

/** Mensaje unico del nombre invalido, para que la ficha y el facturador digan lo mismo. */
export const THIRD_PARTY_NAME_LENGTH_MESSAGE =
    `El nombre debe tener entre ${OTHER_CHARGE_NOMBRE_TERCERO_MIN_LENGTH} y ${OTHER_CHARGE_NOMBRE_TERCERO_MAX_LENGTH} caracteres: Hacienda rechaza un cobro de tercero con un nombre mas corto.`;

const generateLocalId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `oc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

export const generateOtherChargeId = generateLocalId;

const isValidChargeType = (value: unknown): value is string =>
    typeof value === "string" && OTHER_CHARGE_TYPE_OPTIONS.some((option) => option.value === value);

const isIdentificacionShape = (value: unknown): value is OtrosCargosIdentificacion => {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Record<string, unknown>;
    return typeof candidate.tipo === "string" && typeof candidate.numero === "string";
};

const isOtherChargeShape = (value: unknown): value is OtrosCargosElement => {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Record<string, unknown>;
    return (
        isValidChargeType(candidate.tipoDocumentoOC) &&
        typeof candidate.detalle === "string" &&
        (candidate.montoCargo == null || !Number.isNaN(Number(candidate.montoCargo)))
    );
};

const normalizeStoredOtherCharge = (value: unknown): StoredOtherCharge | null => {
    if (!isOtherChargeShape(value)) return null;
    const candidate = value as Partial<StoredOtherCharge>;
    const porcentaje = candidate.porcentajeOC == null ? undefined : Number(candidate.porcentajeOC);
    // Un JSON escrito por una version futura puede no traer `mode`: se infiere del
    // porcentaje para no perder el cargo.
    const mode: OtherChargeAmountMode =
        candidate.mode === "percent" || candidate.mode === "amount"
            ? candidate.mode
            : porcentaje != null
                ? "percent"
                : "amount";
    return {
        id: typeof candidate.id === "string" && candidate.id ? candidate.id : generateLocalId(),
        mode,
        source: candidate.source === "shipping" ? candidate.source : undefined,
        tipoDocumentoOC: `${candidate.tipoDocumentoOC}`,
        tipoDocumentoOTROS: candidate.tipoDocumentoOTROS,
        identificacionTercero: isIdentificacionShape(candidate.identificacionTercero)
            ? candidate.identificacionTercero
            : undefined,
        nombreTercero: candidate.nombreTercero,
        detalle: `${candidate.detalle}`,
        porcentajeOC: porcentaje,
        montoCargo: Number(candidate.montoCargo || 0),
    };
};

export const parseStoredOtherCharges = (
    raw: string | null | undefined,
): StoredOtherCharge[] => {
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
            .map((item) => normalizeStoredOtherCharge(item))
            .filter((item): item is StoredOtherCharge => item !== null);
    } catch {
        return [];
    }
};

export const serializeStoredOtherCharges = (charges: StoredOtherCharge[]): string =>
    JSON.stringify(charges);

/**
 * Proyecta al shape limpio que viaja a la lambda. Poda los campos que el tipo de
 * cargo no admite: un cargo que fue "04" y se edito a "02" no debe arrastrar el
 * tercero, porque Hacienda rechaza el comprobante.
 */
export const toOtrosCargosElement = (charge: StoredOtherCharge): OtrosCargosElement => {
    const element: OtrosCargosElement = {
        tipoDocumentoOC: charge.tipoDocumentoOC,
        detalle: sanitizeSafeText(charge.detalle, OTHER_CHARGE_DETALLE_MAX_LENGTH),
        montoCargo: Number(charge.montoCargo || 0),
    };
    if (charge.tipoDocumentoOC === OTHER_CHARGE_TYPE_OTHERS && charge.tipoDocumentoOTROS) {
        element.tipoDocumentoOTROS = charge.tipoDocumentoOTROS;
    }
    if (charge.tipoDocumentoOC === OTHER_CHARGE_TYPE_THIRD_PARTY) {
        if (charge.identificacionTercero) {
            element.identificacionTercero = {
                tipo: charge.identificacionTercero.tipo,
                numero: charge.identificacionTercero.numero,
            };
        }
        if (charge.nombreTercero) {
            element.nombreTercero = charge.nombreTercero;
        }
    }
    if (charge.mode === "percent" && charge.porcentajeOC != null) {
        element.porcentajeOC = Number(charge.porcentajeOC);
    }
    return element;
};

export const toOtrosCargosElements = (charges: StoredOtherCharge[]): OtrosCargosElement[] =>
    charges.slice(0, MAX_OTHER_CHARGES).map(toOtrosCargosElement);

/**
 * Resuelve el monto de un cargo. `base` es la venta neta del documento
 * (subtotal - descuento), la misma que el XML emite como TotalVentaNeta.
 */
export const resolveChargeAmount = (charge: StoredOtherCharge, base: number): number => {
    if (charge.mode !== "percent") return Number(charge.montoCargo || 0);
    const porcentaje = Number(charge.porcentajeOC || 0);
    return Number(toNumberFormat((Number(base || 0) * porcentaje) / 100));
};

/**
 * Devuelve la lista con `montoCargo` ya resuelto. Es la unica forma legitima de
 * leer los montos: nadie debe leer `montoCargo` crudo de un cargo porcentual.
 * Conserva la identidad del arreglo cuando nada cambio, para no invalidar en cada
 * render al selector memoizado del resumen.
 */
export const resolveOtherCharges = (
    charges: StoredOtherCharge[] | undefined,
    base: number,
): StoredOtherCharge[] => {
    if (!charges || charges.length === 0) return charges || [];
    let changed = false;
    const resolved = charges.map((charge) => {
        const montoCargo = resolveChargeAmount(charge, base);
        if (montoCargo === Number(charge.montoCargo || 0)) return charge;
        changed = true;
        return { ...charge, montoCargo };
    });
    return changed ? resolved : charges;
};

export const totalOtherCharges = (
    charges: StoredOtherCharge[] | undefined,
    base: number,
): number => {
    if (!charges || charges.length === 0) return 0;
    const total = charges
        .slice(0, MAX_OTHER_CHARGES)
        .reduce((acc, charge) => acc + resolveChargeAmount(charge, base), 0);
    return Number(toNumberFormat(total));
};

/**
 * Suma los `montoCargo` tal como quedaron persistidos, sin volver a resolver los
 * porcentuales. Es lo que debe viajar a Hacienda: `TotalOtrosCargos` tiene que cuadrar
 * exactamente con la suma de los `MontoCargo` que se emiten en los nodos, y ambos salen
 * de la misma lista guardada.
 */
export const sumOtherChargesAmounts = (charges: StoredOtherCharge[] | undefined): number => {
    if (!charges || charges.length === 0) return 0;
    const total = charges
        .slice(0, MAX_OTHER_CHARGES)
        .reduce((acc, charge) => acc + Number(charge.montoCargo || 0), 0);
    return Number(toNumberFormat(total));
};

/** Lee el total de cargos de un documento ya persistido (impresion y reportes). */
export const readOtherChargesTotal = (
    otrosCargos: string | null | undefined,
    base: number,
): number => totalOtherCharges(parseStoredOtherCharges(otrosCargos), base);

/** Aplana un cargo al shape que consume el formulario dinamico. */
export const toOtherChargeFormData = (charge: StoredOtherCharge): OtherChargeFormData => ({
    tipoDocumentoOC: charge.tipoDocumentoOC,
    tipoDocumentoOTROS: charge.tipoDocumentoOTROS ?? "",
    identificacionTipo: charge.identificacionTercero?.tipo ?? "",
    identificacionNumero: charge.identificacionTercero?.numero ?? "",
    nombreTercero: charge.nombreTercero ?? "",
    detalle: charge.detalle,
    mode: charge.mode,
    porcentajeOC: charge.porcentajeOC ?? null,
    montoCargo: charge.montoCargo ?? 0,
});

/**
 * Re-anida los datos del formulario en un cargo. No poda los campos que el tipo no admite:
 * de eso se encarga `toOtrosCargosElement` justo antes de enviarlos, que es el unico punto
 * por donde salen hacia Hacienda.
 */
export const fromOtherChargeFormData = (
    formData: OtherChargeFormData,
    id: string,
): StoredOtherCharge => {
    const mode: OtherChargeAmountMode = `${formData.mode}` === "percent" ? "percent" : "amount";
    const tipo = `${formData.identificacionTipo || ""}`;
    const numero = `${formData.identificacionNumero || ""}`;
    return {
        id,
        mode,
        tipoDocumentoOC: `${formData.tipoDocumentoOC || ""}`,
        tipoDocumentoOTROS: formData.tipoDocumentoOTROS ? `${formData.tipoDocumentoOTROS}` : undefined,
        identificacionTercero: tipo || numero ? { tipo, numero } : undefined,
        nombreTercero: formData.nombreTercero ? `${formData.nombreTercero}`.trim() : undefined,
        detalle: `${formData.detalle || ""}`,
        porcentajeOC: mode === "percent" ? Number(formData.porcentajeOC || 0) : undefined,
        montoCargo: mode === "percent" ? 0 : Number(formData.montoCargo || 0),
    };
};

/**
 * Convierte el `impuestos_servicio` de un documento historico en el cargo tipo 06 que
 * siempre debio ser. Hasta v4.4 ese monto viajaba como una linea de producto exenta
 * llamada "propina", lo que contaminaba TotalServExentos y dejaba TotalOtrosCargos en 0.
 *
 * El id es estable (no aleatorio) porque este cargo se deriva del documento en cada
 * lectura: no se persiste ni se reescribe el historico.
 */
export const legacyServiceTaxCharge = (impuestoServicio: number): StoredOtherCharge => ({
    id: "legacy-impuestos-servicio",
    mode: "amount",
    tipoDocumentoOC: OTHER_CHARGE_TYPE_SERVICE_TAX,
    detalle: getOtherChargeTypeLabel(OTHER_CHARGE_TYPE_SERVICE_TAX),
    montoCargo: Number(toNumberFormat(Number(impuestoServicio || 0))),
});

/**
 * Cargo de impuesto de servicio en modo porcentaje. Lo usan el preset del editor y la
 * precarga por suscripcion (`aplicar_imp_servicio` / `imp_servicio` del perfil).
 * `montoCargo` nace en 0 y lo resuelve el selector del resumen contra la venta neta.
 */
export const serviceTaxCharge = (porcentaje: number): StoredOtherCharge => ({
    id: generateLocalId(),
    mode: "percent",
    tipoDocumentoOC: OTHER_CHARGE_TYPE_SERVICE_TAX,
    detalle: getOtherChargeTypeLabel(OTHER_CHARGE_TYPE_SERVICE_TAX),
    porcentajeOC: Number(porcentaje || 0),
    montoCargo: 0,
});

/**
 * Otros cargos de un documento ya persistido. Un documento anterior a v4.4 no tiene
 * `otros_cargos` pero puede traer `impuestos_servicio`: se convierte al vuelo. Los
 * documentos nuevos nacen con `otros_cargos` y nunca entran por esa rama.
 */
export const readDocumentOtherCharges = (document: {
    otros_cargos?: string | null;
    impuestos_servicio?: number | null;
}): StoredOtherCharge[] => {
    const stored = parseStoredOtherCharges(document.otros_cargos);
    if (stored.length > 0) return stored;
    const impuestoServicio = Number(document.impuestos_servicio || 0);
    return impuestoServicio > 0 ? [legacyServiceTaxCharge(impuestoServicio)] : [];
};

/**
 * Total de otros cargos de un documento persistido, para impresion y reportes. Suma los
 * montos guardados (no los re-resuelve) para que lo impreso sea exactamente lo que se
 * envio a Hacienda, e incluye el `impuestos_servicio` de los documentos historicos.
 */
export const readDocumentOtherChargesTotal = (document: {
    otros_cargos?: string | null;
    impuestos_servicio?: number | null;
}): number => sumOtherChargesAmounts(readDocumentOtherCharges(document));

/** Crea un cargo vacio listo para editar, con el detalle precargado por tipo. */
export const createEmptyOtherCharge = (tipoDocumentoOC: string): StoredOtherCharge => ({
    id: generateLocalId(),
    mode: "amount",
    tipoDocumentoOC,
    detalle: getOtherChargeTypeLabel(tipoDocumentoOC),
    montoCargo: 0,
});
